import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { ArrowLeftRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import logoAsset from "@/assets/balham-babes-logo.svg.asset.json";
import { useWorkspaceMode } from "@/hooks/useWorkspaceMode";
import { BUSINESS_TABS, isBusinessTab } from "./businessTabs";
import { cn } from "@/lib/utils";
import { NotificationBell } from "@/components/NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Card, CardContent } from "@/components/ui/card";
import { UserProfile } from "@/components/auth/UserProfile";
import { useAuth } from "@/hooks/useAuth";
import { useUserProfile } from "@/hooks/useUserProfile";
import { optimizeImage } from "@/lib/imageUrl";
import { useConversations } from "@/hooks/useConversations";

export const BusinessNavigation = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const { setMode } = useWorkspaceMode();
  const { totalUnread: messageUnread } = useConversations();
  const [showMobileProfile, setShowMobileProfile] = useState(false);
  const [showDesktopProfile, setShowDesktopProfile] = useState(false);
  const tabParam = params.get("tab");
  const active = isBusinessTab(tabParam) ? tabParam : "listings";

  const toMember = () => {
    setMode("member");
    navigate("/activities");
  };

  return (
    <>
    <header className="border-b border-border/50 bg-background/80 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-2 sm:gap-3">
        <Link to="/business" className="flex min-w-0 items-center gap-2 no-underline md:shrink-0">
          <img src={logoAsset.url} alt="Balham Babes" className="h-7 sm:h-8 w-auto min-w-0 object-contain" />
          <Badge className="hidden min-[360px]:inline-flex shrink-0 text-[9px] font-bold uppercase text-primary-foreground bg-primary border-0 px-1.5 py-0.5">
            Business
          </Badge>
        </Link>

        <nav className="hidden md:flex items-center gap-0.5">
          {BUSINESS_TABS.map((t) => (
            <Button
              key={t.id}
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/business?tab=${t.id}`)}
              className={cn(
                "text-sm font-medium",
                active === t.id ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              {t.label}
            </Button>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-1">
          <Button variant="outline" size="sm" onClick={toMember} aria-label="Switch to member site" className="hidden md:inline-flex mr-1 gap-1.5 rounded-full px-2.5 sm:px-3">
            <ArrowLeftRight className="h-4 w-4 shrink-0" />
            <span className="hidden text-sm sm:inline">Member site</span>
          </Button>
          <NotificationBell />
          <button
            onClick={() => navigate("/business?tab=messages")}
            className={cn(
              "relative flex items-center justify-center rounded-md p-2 transition-colors",
              active === "messages"
                ? "text-primary bg-primary/10"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
            aria-label="Messages"
          >
            <MessageCircle className="h-5 w-5" />
            {messageUnread > 0 && (
              <Badge className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center border-0 bg-primary p-0 px-1 text-[9px] text-primary-foreground">
                {messageUnread > 9 ? "9+" : messageUnread}
              </Badge>
            )}
          </button>
          {user && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => window.matchMedia("(min-width: 768px)").matches
                ? setShowDesktopProfile((open) => !open)
                : setShowMobileProfile(true)}
              aria-label="Open business profile menu"
              className="h-10 w-10 shrink-0 rounded-lg p-0"
            >
              <Avatar className="h-9 w-9 rounded-lg">
                <AvatarImage src={optimizeImage(profile?.avatar_url, { width: 72, height: 72, resize: "cover" }) || undefined} alt={profile?.first_name || "User"} className="rounded-lg" />
                <AvatarFallback className="rounded-lg bg-primary text-sm text-primary-foreground">{profile?.first_name?.charAt(0)?.toUpperCase() || "U"}</AvatarFallback>
              </Avatar>
            </Button>
          )}
        </div>
      </div>
    </header>
    {user && (
      <>
        <Sheet open={showMobileProfile} onOpenChange={setShowMobileProfile}>
          <SheetContent side="right" className="w-full overflow-y-auto pt-12 sm:max-w-md">
            <SheetHeader className="pr-8"><SheetTitle>Business account</SheetTitle></SheetHeader>
            <div className="mt-4 pb-20"><UserProfile userId={user.id} onNavigate={() => setShowMobileProfile(false)} /></div>
          </SheetContent>
        </Sheet>
        {showDesktopProfile && (
          <div className="relative hidden md:block">
            <div className="absolute right-4 top-0 z-50 w-80">
              <Card className="border-0 bg-card shadow-soft">
                <CardContent className="p-4">
                  <UserProfile userId={user.id} onNavigate={() => setShowDesktopProfile(false)} />
                  <Button variant="ghost" size="sm" onClick={() => setShowDesktopProfile(false)} className="mt-2 w-full">Close</Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </>
    )}
    </>
  );
};
